// src/pages/BlogPostPage.js
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { parseTags, asString } from '../lib/tagHelpers';
import logger from '../utils/logger';

// --- helpers ---
const toText = (v) => (typeof v === 'string' ? v : v == null ? '' : String(v));

/**
 * Very small markdown-lite renderer:
 * - headings (#, ##, ###)
 * - fenced code blocks ```...```
 * - inline code `...`
 * - unordered lists (- / *)
 * - paragraphs + newlines
 */
const MarkdownLite = ({ source }) => {
  if (!source) return null;

  // Some rows may have the literal backslash-n in them. Unescape once.
  let text = toText(source);
  if (text.includes('\\n') && !text.includes('\n')) {
    text = text.replace(/\\n/g, '\n');
  }

  // Split into blocks by fenced code ```...```
  const parts = [];
  const fence = /```([\s\S]*?)```/g;
  let last = 0,
    m;
  while ((m = fence.exec(text)) !== null) {
    if (m.index > last)
      parts.push({ type: 'text', value: text.slice(last, m.index) });
    parts.push({ type: 'code', value: m[1] });
    last = fence.lastIndex;
  }
  if (last < text.length) parts.push({ type: 'text', value: text.slice(last) });

  // render helpers
  const renderInline = (s, keyBase) => {
    // inline code: `code`
    const chunks = [];
    const re = /`([^`]+)`/g;
    let i = 0,
      j,
      prev = 0;
    while ((j = re.exec(s)) !== null) {
      if (j.index > prev)
        chunks.push(
          <span key={`${keyBase}-t-${i++}`}>{s.slice(prev, j.index)}</span>
        );
      chunks.push(
        <code
          key={`${keyBase}-c-${i++}`}
          style={{
            background: '#151515',
            padding: '0 4px',
            borderRadius: 4,
            border: '1px solid #333',
            fontFamily:
              'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
          }}
        >
          {j[1]}
        </code>
      );
      prev = re.lastIndex;
    }
    if (prev < s.length)
      chunks.push(<span key={`${keyBase}-t-${i++}`}>{s.slice(prev)}</span>);
    return chunks;
  };

  const renderTextBlock = (block, k) => {
    // break into lines
    const lines = block.split(/\r?\n/);

    const nodes = [];
    let i = 0;

    while (i < lines.length) {
      const line = lines[i];

      // headings
      const h = /^(#{1,3})\s+(.*)$/.exec(line);
      if (h) {
        const level = h[1].length;
        const content = h[2];
        const commonStyle = {
          color: '#f0f0f0',
          margin: '1.2rem 0 0.6rem',
          lineHeight: 1.25,
        };
        if (level === 1)
          nodes.push(
            <h2
              key={`h-${k}-${i}`}
              style={{
                ...commonStyle,
                fontSize: 'clamp(1.4rem,4.5vw,2rem)',
                color: '#e07a5f',
              }}
            >
              {content}
            </h2>
          );
        else if (level === 2)
          nodes.push(
            <h3
              key={`h-${k}-${i}`}
              style={{
                ...commonStyle,
                fontSize: 'clamp(1.2rem,3.5vw,1.5rem)',
              }}
            >
              {content}
            </h3>
          );
        else
          nodes.push(
            <h4
              key={`h-${k}-${i}`}
              style={{
                ...commonStyle,
                fontSize: 'clamp(1.05rem,3vw,1.2rem)',
              }}
            >
              {content}
            </h4>
          );
        i++;
        continue;
      }

      // list block (consume consecutive -/* lines)
      if (/^\s*[-*]\s+/.test(line)) {
        const items = [];
        while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) {
          const itemText = lines[i].replace(/^\s*[-*]\s+/, '');
          items.push(
            <li key={`li-${k}-${i}`} style={{ margin: '0.25rem 0' }}>
              {renderInline(itemText, `li-${k}-${i}`)}
            </li>
          );
          i++;
        }
        nodes.push(
          <ul
            key={`ul-${k}-${i}`}
            style={{
              paddingLeft: '1.25rem',
              margin: '0.5rem 0 0.75rem',
              listStyle: 'disc',
              color: '#ddd',
            }}
          >
            {items}
          </ul>
        );
        continue;
      }

      // paragraph (consume until blank line)
      let para = line;
      i++;
      while (i < lines.length && lines[i].trim() !== '') {
        para += '\n' + lines[i];
        i++;
      }
      // skip the blank line separator
      if (i < lines.length && lines[i].trim() === '') i++;

      // empty paragraph?
      if (para.trim() === '') {
        nodes.push(
          <div key={`sp-${k}-${i}`} style={{ height: '0.6rem' }} />
        );
      } else {
        nodes.push(
          <p
            key={`p-${k}-${i}`}
            style={{
              color: '#ddd',
              lineHeight: 1.7,
              margin: '0.75rem 0',
              fontSize: 'clamp(0.95rem,2.8vw,1.05rem)',
            }}
          >
            {renderInline(para, `p-${k}-${i}`)}
          </p>
        );
      }
    }
    return nodes;
  };

  return (
    <div>
      {parts.map((p, idx) => {
        if (p.type === 'code') {
          return (
            <pre
              key={`code-${idx}`}
              style={{
                background: '#0f0f0f',
                color: '#ddd',
                border: '1px solid #2a2a2a',
                borderRadius: 12,
                padding: '1rem',
                overflowX: 'auto',
                fontSize: '0.95rem',
                lineHeight: 1.6,
              }}
            >
              <code
                style={{
                  fontFamily:
                    'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                }}
              >
                {p.value}
              </code>
            </pre>
          );
        }
        // text block
        return (
          <React.Fragment key={`txt-${idx}`}>
            {renderTextBlock(p.value, idx)}
          </React.Fragment>
        );
      })}
    </div>
  );
};
// --- end markdown-lite ---

const estimateReadingTime = (text) => {
  if (!text) return 0;
  // strip fenced code for a fairer read time
  const stripped = toText(text).replace(/```[\s\S]*?```/g, ' ');
  const words = stripped.trim().split(/\s+/).filter(Boolean).length;
  const WPM = 220; // comfy on-screen read
  return Math.max(1, Math.round(words / WPM));
};

const BlogPostPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [adjacent, setAdjacent] = useState({ prev: null, next: null });

  // load the post
  useEffect(() => {
    (async () => {
      try {
        const nowIso = new Date().toISOString();

        const { data, error } = await supabase
          .from('blog_posts')
          .select('*')
          .eq('slug', slug)
          .eq('published', true)
          .lte('publish_at', nowIso) // Only show posts that should be visible
          .single();

        if (error || !data) {
          setNotFound(true);
          return;
        }

        const processedPost = { ...data, tags: parseTags(data.tags) };
        setPost(processedPost);
      } catch (err) {
        console.error('Error loading post:', err);
        setNotFound(true);
      }
    })();
  }, [slug]);

  // SEO meta + structured data
  useEffect(() => {
    if (!post) return;

    const title = asString(post.seo_title || post.title);
    const publishDate = asString(post.publish_at || post.created_at);
    const updatedDate = asString(post.updated_at || publishDate);

    document.title = `${title} | WhyDidItDie`;

    const setMetaTag = (name, content, isProperty = false) => {
      if (!content) return;
      const attribute = isProperty ? 'property' : 'name';
      let el = document.querySelector(`meta[${attribute}="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attribute, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', asString(content));
    };

    setMetaTag('description', post.seo_description || post.excerpt);
    setMetaTag('og:title', post.title, true);
    setMetaTag('og:description', post.excerpt || '', true);
    setMetaTag('og:image', post.cover_image || '', true);
    setMetaTag('og:type', 'article', true);
    setMetaTag(
      'og:url',
      `https://app.whydiditdie.com/blog/${asString(post.slug)}`,
      true
    );
    setMetaTag('twitter:card', 'summary_large_image');
    setMetaTag('twitter:title', post.title);
    setMetaTag('twitter:description', post.excerpt || '');
    setMetaTag('twitter:image', post.cover_image || '');
    setMetaTag('article:published_time', publishDate, true);
    setMetaTag('article:author', post.author || 'Isabella Ibeji', true);

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute(
      'href',
      `https://app.whydiditdie.com/blog/${asString(post.slug)}`
    );

    let script = document.querySelector('script[type="application/ld+json"]');
    if (!script) {
      script = document.createElement('script');
      script.setAttribute('type', 'application/ld+json');
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: asString(post.title),
      image: asString(post.cover_image) || '',
      author: {
        '@type': 'Person',
        name: asString(post.author || 'Isabella Ibeji'),
      },
      publisher: {
        '@type': 'Organization',
        name: 'WhyDidItDie',
        logo: {
          '@type': 'ImageObject',
          url: 'https://app.whydiditdie.com/logo.png',
        },
      },
      datePublished: publishDate,
      dateModified: updatedDate,
      description: asString(post.excerpt) || '',
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `https://app.whydiditdie.com/blog/${asString(post.slug)}`,
      },
    });
  }, [post]);

  // increment views
  useEffect(() => {
    if (!post) return;
    (async () => {
      try {
        await supabase
          .from('blog_posts')
          .update({ views: (post.views || 0) + 1 })
          .eq('id', post.id);
      } catch (e) {
        logger.warn('View update failed (non-blocking):', e);
      }
    })();
  }, [post]);

  // load prev/next posts by publish_at - ONLY SHOW VISIBLE POSTS
  useEffect(() => {
    if (!post) return;
    (async () => {
      try {
        const pivot = post.publish_at || post.created_at;
        const nowIso = new Date().toISOString();

        const [{ data: newer }, { data: older }] = await Promise.all([
          // next = newer post (publish_at greater than current), earliest of the newer
          supabase
            .from('blog_posts')
            .select('id,title,slug,publish_at,published')
            .eq('published', true)
            .lte('publish_at', nowIso)
            .gt('publish_at', pivot)
            .order('publish_at', { ascending: true })
            .limit(1),
          // prev = older post (publish_at less than current), latest of the older
          supabase
            .from('blog_posts')
            .select('id,title,slug,publish_at,published')
            .eq('published', true)
            .lte('publish_at', nowIso)
            .lt('publish_at', pivot)
            .order('publish_at', { ascending: false })
            .limit(1),
        ]);

        setAdjacent({
          prev: older && older.length ? older[0] : null,
          next: newer && newer.length ? newer[0] : null,
        });
      } catch (e) {
        logger.warn('Adjacent posts fetch failed:', e);
      }
    })();
  }, [post]);

  const readingMins = useMemo(
    () => estimateReadingTime(post?.content),
    [post?.content]
  );

  if (notFound) {
    return (
      <div
        style={{
          backgroundColor: '#0a0a0a',
          color: 'white',
          minHeight: '100vh',
          padding: 'clamp(2rem, 6vw, 4rem) 1rem',
        }}
      >
        <div
          style={{
            maxWidth: '800px',
            margin: '0 auto',
            padding: '0 1rem',
          }}
        >
          <h1
            style={{
              fontSize: 'clamp(1.75rem, 5vw, 2.25rem)',
              color: '#e07a5f',
              marginBottom: '1rem',
            }}
          >
            Post not found
          </h1>
          <Link
            to="/blog"
            style={{
              color: '#e07a5f',
              textDecoration: 'none',
              fontSize: 'clamp(0.9rem, 2.5vw, 1rem)',
            }}
          >
            ← Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div
        style={{
          backgroundColor: '#0a0a0a',
          color: 'white',
          minHeight: '100vh',
          padding: 'clamp(2rem, 6vw, 4rem) 1rem',
        }}
      >
        <p
          style={{
            textAlign: 'center',
            fontSize: 'clamp(0.9rem, 2.5vw, 1rem)',
          }}
        >
          Loading…
        </p>
      </div>
    );
  }

  const tags = post.tags;
  const displayDate = post.publish_at || post.created_at;

  return (
    <div
      style={{
        backgroundColor: '#0a0a0a',
        color: 'white',
        minHeight: '100vh',
        padding: 'clamp(2rem, 6vw, 4rem) clamp(1rem, 4vw, 2rem)',
      }}
    >
      <div
        style={{
          maxWidth: '880px',
          margin: '0 auto',
          padding: '0 clamp(0.5rem, 2vw, 1rem)',
        }}
      >
        <Link
          to="/blog"
          style={{
            color: '#e07a5f',
            textDecoration: 'none',
            marginBottom: '2rem',
            display: 'inline-block',
            fontSize: 'clamp(0.9rem, 2.5vw, 1rem)',
          }}
        >
          ← Back to Blog
        </Link>

        <h1
          style={{
            fontSize: 'clamp(1.75rem, 6vw, 3rem)',
            color: '#e07a5f',
            margin: '1rem 0',
            lineHeight: 1.2,
            wordWrap: 'break-word',
          }}
        >
          {asString(post.title)}
        </h1>

        <p
          style={{
            color: '#ccc',
            marginBottom: '1rem',
            fontSize: 'clamp(0.85rem, 2.5vw, 1rem)',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '0.5rem',
            alignItems: 'center',
          }}
        >
          <span>{new Date(displayDate).toLocaleDateString()}</span>
          <span>·</span>
          <span>{asString(post.author || 'WDID')}</span>
          {readingMins ? (
            <>
              <span>·</span>
              <span>{readingMins} min read</span>
            </>
          ) : null}
        </p>

        {tags && tags.length > 0 && (
          <div
            style={{
              display: 'flex',
              gap: '8px',
              flexWrap: 'wrap',
              marginBottom: '2rem',
            }}
          >
            {tags.map((tag, idx) => (
              <span
                key={idx}
                style={{
                  fontSize: 'clamp(0.75rem, 2vw, 0.85rem)',
                  padding: '4px 10px',
                  backgroundColor: 'rgba(224, 122, 95, 0.1)',
                  color: '#f4a261',
                  border: '1px solid #333',
                  borderRadius: 4,
                }}
              >
                #{String(tag)}
              </span>
            ))}
          </div>
        )}

        {post.cover_image && (
          <img
            src={asString(post.cover_image)}
            alt={asString(post.title)}
            loading="lazy"
            style={{
              width: '100%',
              height: 'auto',
              borderRadius: 12,
              marginBottom: '2rem',
              display: 'block',
              border: '1px solid #2a2a2a',
            }}
            onError={(e) => {
              // fail gracefully if image 404s
              e.currentTarget.style.display = 'none';
            }}
          />
        )}

        {/* BODY: markdown-lite */}
        <div
          style={{
            fontSize: 'clamp(0.95rem, 2.8vw, 1.05rem)',
          }}
        >
          <MarkdownLite source={post.content} />
        </div>

        {/* Adjacent navigation */}
        {(adjacent.prev || adjacent.next) && (
          <div
            style={{
              borderTop: '1px solid #2a2a2a',
              marginTop: '2.25rem',
              paddingTop: '1.25rem',
              display: 'flex',
              justifyContent: 'space-between',
              gap: '1rem',
              flexWrap: 'wrap',
            }}
          >
            <div style={{ minWidth: 220, flex: 1 }}>
              {adjacent.prev ? (
                <Link
                  to={`/blog/${asString(adjacent.prev.slug)}`}
                  style={{
                    color: '#e07a5f',
                    textDecoration: 'none',
                    display: 'inline-flex',
                    gap: '0.5rem',
                    alignItems: 'center',
                  }}
                >
                  ← Prev: {asString(adjacent.prev.title)}
                </Link>
              ) : (
                <span style={{ color: '#666' }} />
              )}
            </div>
            <div style={{ textAlign: 'right', minWidth: 220, flex: 1 }}>
              {adjacent.next ? (
                <Link
                  to={`/blog/${asString(adjacent.next.slug)}`}
                  style={{
                    color: '#e07a5f',
                    textDecoration: 'none',
                    display: 'inline-flex',
                    gap: '0.5rem',
                    alignItems: 'center',
                  }}
                >
                  Next: {asString(adjacent.next.title)} →
                </Link>
              ) : (
                <span style={{ color: '#666' }} />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogPostPage;
